import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Menu, X, Instagram, MapPin, Phone, Mail, Clock, Wrench, Coffee, Bike, ArrowUpRight, Send, BadgeCheck, Zap } from "lucide-react";
import { Toaster, toast } from "sonner";
import axios from "axios";
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IMAGES = {
  hero: "https://images.unsplash.com/photo-1648805495738-ee12156c2195?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxncmF2ZWwlMjBiaWtlJTIwY3ljbGlzdHxlbnwwfHx8fDE3Nzc4Mjg5OTV8MA&ixlib=rb-4.1.0&q=85",
  cafeInterior: "https://images.pexels.com/photos/37025770/pexels-photo-37025770.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  coffee: "https://images.unsplash.com/photo-1634234498465-a3a63b112256?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHxzcGVjaWFsdHklMjBjb2ZmZWUlMjBlc3ByZXNzb3xlbnwwfHx8fDE3Nzc4Mjg5OTV8MA&ixlib=rb-4.1.0&q=85",
  workshop: "https://images.unsplash.com/photo-1675798226758-a2e241b376fb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxiaWN5Y2xlJTIwcmVwYWlyJTIwd29ya3Nob3B8ZW58MHx8fHwxNzc3ODI4OTk1fDA&ixlib=rb-4.1.0&q=85",
  cyclistLifestyle: "https://images.unsplash.com/photo-1631090579214-61d8b4ceaa04?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwzfHxncmF2ZWwlMjBiaWtlJTIwY3ljbGlzdHxlbnwwfHx8fDE3Nzc4Mjg5OTV8MA&ixlib=rb-4.1.0&q=85",
  bikeGravel: "https://customer-assets.emergentagent.com/job_cafe-velo-station/artifacts/ygqndi72_image.png",
  bikeMtb: "https://customer-assets.emergentagent.com/job_cafe-velo-station/artifacts/kg4ys9vy_photo%20mondraker%202.jpg",
  bikeFolding: "https://customer-assets.emergentagent.com/job_cafe-velo-station/artifacts/7ibbjqcz_PRO20_Julien_Francfort_13%20-%20Moyenne.jpeg",
  bikeUrban: "https://customer-assets.emergentagent.com/job_cafe-velo-station/artifacts/rhkc3txd_photo%20welt%20bike.jpg",
  bikeShowroom: "https://images.pexels.com/photos/13415391/pexels-photo-13415391.jpeg?auto=compress&cs=tinysrgb&w=1600",
  bikeCargo: "https://customer-assets.emergentagent.com/job_cafe-velo-station/artifacts/b256mpb8_benno-passenger-pack.jpg.webp",
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

const NAV = [
  { label: "L'Atelier", href: "#atelier" },
  { label: "Vélos", href: "#velos" },
  { label: "Le Café", href: "#cafe" },
  { label: "Galerie", href: "#galerie" },
  { label: "Contact", href: "#contact" },
];

// Reusable animation configs (extracted to avoid new object creation on every render)
const FADE_IN_IMAGE = {
  initial: { opacity: 0 },
  whileInView: { opacity: 1 },
  transition: { duration: 0.8 },
  viewport: { once: true },
};

const VIEWPORT_ONCE_30 = { once: true, amount: 0.3 };
const VIEWPORT_ONCE_20 = { once: true, amount: 0.2 };
const VIEWPORT_ONCE = { once: true };

const GALLERY_INITIAL = { opacity: 0, y: 20 };
const GALLERY_WHILE_IN_VIEW = { opacity: 1, y: 0 };
const galleryTransition = (i) => ({ duration: 0.7, delay: i * 0.05 });

function DesktopNav() {
  return (
    <nav className="hidden md:flex items-center gap-8">
      {NAV.map((item) => (
        <a
          key={item.href}
          href={item.href}
          data-testid={`nav-${item.href.replace("#", "")}`}
          className="text-sm font-medium text-[#1C1C1C] hover:text-[#C46D5E] transition-colors"
        >
          {item.label}
        </a>
      ))}
    </nav>
  );
}

function MobileMenu({ onClose }) {
  return (
    <div className="md:hidden glass-header border-t border-[#E5E3DB]/60" data-testid="mobile-menu">
      <div className="flex flex-col px-6 py-4 gap-4">
        {NAV.map((item) => (
          <a
            key={item.href}
            href={item.href}
            onClick={onClose}
            className="text-[#1C1C1C] py-2 font-medium"
          >
            {item.label}
          </a>
        ))}
        <a
          href="#contact"
          onClick={onClose}
          className="btn-primary text-center px-5 py-3 rounded-full text-sm font-semibold"
        >
          Nous écrire
        </a>
      </div>
    </div>
  );
}

function HeaderLogo() {
  return (
    <a href="#top" data-testid="logo-link" className="flex items-center gap-2">
      <div className="h-8 w-8 rounded-full bg-[#1C1C1C] flex items-center justify-center">
        <Bike className="h-4 w-4 text-[#FAF9F6]" />
      </div>
      <span className="font-display text-xl font-bold tracking-tight">My Station</span>
    </a>
  );
}

function HeaderActions({ open, setOpen }) {
  return (
    <>
      <a
        href="#contact"
        data-testid="header-cta-button"
        className="hidden md:inline-flex items-center gap-2 btn-primary px-5 py-2.5 rounded-full text-sm font-semibold"
      >
        Nous écrire <ArrowUpRight className="h-4 w-4" />
      </a>

      <button
        className="md:hidden p-2"
        data-testid="mobile-menu-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-label="Toggle menu"
      >
        {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
      </button>
    </>
  );
}

function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Scroll listener: `onScroll` is defined inside the effect (closure) and
  // `setScrolled` is a stable React setter — no reactive deps needed.
  // codereview-exclude: react-hooks/exhaustive-deps (false positive)
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-header" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-5 flex items-center justify-between">
        <HeaderLogo />
        <DesktopNav />
        <HeaderActions open={open} setOpen={setOpen} />
      </div>
      {open && <MobileMenu onClose={() => setOpen(false)} />}
    </header>
  );
}

function HeroText() {
  return (
    <motion.div
      className="lg:col-span-7 flex flex-col justify-end"
      initial="hidden"
      animate="visible"
      variants={fadeUp}
    >
      <span className="eyebrow">Vélociste · Atelier · Juvisy-sur-Orge</span>
      <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-bold mt-5 leading-[1.02]">
        Vélociste,<br/>
        <span className="text-[#C46D5E]">atelier &amp; café.</span>
      </h1>
      <p className="text-base md:text-lg text-[#595959] mt-6 max-w-xl leading-relaxed">
        My Station, c'est avant tout un atelier mécanique et un showroom de vélos triés sur le
        volet — Cinelli, Mondraker, WELT, UTO, Benno. On y répare, on y règle, on y vend.
        Et le café est offert pendant que vous patientez.
      </p>
      <div className="flex flex-wrap gap-3 mt-8">
        <a href="#velos" data-testid="hero-velos-cta" className="btn-primary px-6 py-3 rounded-full text-sm font-semibold inline-flex items-center gap-2">
          Voir nos vélos <ArrowUpRight className="h-4 w-4" />
        </a>
        <a href="#atelier" data-testid="hero-atelier-cta" className="btn-ghost px-6 py-3 rounded-full text-sm font-semibold">
          Prendre rendez-vous atelier
        </a>
      </div>
    </motion.div>
  );
}

function HeroMedia() {
  return (
    <motion.div
      className="lg:col-span-5 grid grid-cols-2 gap-4"
      initial="hidden"
      animate="visible"
      variants={fadeUp}
      custom={1}
    >
      <div className="image-zoom col-span-2 h-64 md:h-80 relative bg-black overflow-hidden">
        {/* YouTube embed, autoplay muted loop, no controls — visible on all devices */}
        <iframe
          title="My Station — vidéo d'ambiance"
          data-testid="hero-video"
          src="https://www.youtube-nocookie.com/embed/A4zsZS3bmTw?autoplay=1&mute=1&loop=1&playlist=A4zsZS3bmTw&controls=0&showinfo=0&modestbranding=1&rel=0&iv_load_policy=3&playsinline=1&disablekb=1"
          allow="autoplay; encrypted-media"
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[177.78vh] min-w-full h-[56.25vw] min-h-full pointer-events-none border-0"
        />
      </div>
      <div className="image-zoom h-40 md:h-48">
        <img src={IMAGES.workshop} alt="Atelier vélo" className="w-full h-full object-cover" />
      </div>
      <div className="bento-card flex flex-col justify-between p-5">
        <Wrench className="h-6 w-6 text-[#C46D5E]" />
        <div>
          <p className="font-display text-3xl font-bold leading-none">5</p>
          <p className="text-xs text-[#595959] mt-1">Marques partenaires</p>
        </div>
      </div>
    </motion.div>
  );
}

function Hero() {
  return (
    <section id="top" className="relative pt-32 md:pt-40 pb-16 md:pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          <HeroText />
          <HeroMedia />
        </div>
      </div>
    </section>
  );
}

const ABOUT_STATS = [
  { value: "5", label: "Marques partenaires" },
  { value: "15+", label: "Modèles en showroom" },
  { value: "∞", label: "Kilomètres partagés" },
];

function AboutStats() {
  return (
    <div className="grid grid-cols-3 gap-6 mt-10 pt-10 border-t border-[#E5E3DB]">
      {ABOUT_STATS.map((s) => (
        <div key={s.label}>
          <p className="font-display text-3xl font-bold text-[#1C1C1C]">{s.value}</p>
          <p className="text-xs text-[#595959] mt-2 uppercase tracking-wider">{s.label}</p>
        </div>
      ))}
    </div>
  );
}

function AboutContent() {
  return (
    <motion.div
      className="lg:col-span-7"
      initial="hidden"
      whileInView="visible"
      viewport={VIEWPORT_ONCE_30}
      variants={fadeUp}
    >
      <span className="eyebrow">Notre histoire</span>
      <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-semibold mt-4">
        Vélociste indépendant à Juvisy.
      </h2>
      <p className="text-[#595959] mt-6 leading-relaxed">
        My Station est avant tout un atelier mécanique et un showroom. Nous sélectionnons des
        showroom. Nous sélectionnons des marques que nous roulons nous-mêmes —
        <strong className="font-semibold text-[#1C1C1C]"> Cinelli</strong> pour le gravel et l'urbain premium,
        <strong className="font-semibold text-[#1C1C1C]"> Mondraker</strong> pour le VTT haut de gamme,
        <strong className="font-semibold text-[#1C1C1C]"> WELT</strong> pour le gravel et le VTT polyvalents,
        <strong className="font-semibold text-[#1C1C1C]"> UTO</strong> pour le pliant urbain,
        <strong className="font-semibold text-[#1C1C1C]"> Benno</strong> pour les cargos et VAE familiaux.
      </p>
      <p className="text-[#595959] mt-4 leading-relaxed">
        À côté du comptoir mécanique, un café de spécialité offre une pause naturelle pendant
        les essais et les révisions. Ride with mates.
      </p>
      <AboutStats />
    </motion.div>
  );
}

function About() {
  return (
    <section className="py-20 md:py-32 bg-[#F2F0EA]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <motion.div
            className="lg:col-span-5 image-zoom h-[420px]"
            {...FADE_IN_IMAGE}
          >
            <img src={IMAGES.workshop} alt="Atelier mécanique" className="w-full h-full object-cover" />
          </motion.div>
          <AboutContent />
        </div>
      </div>
    </section>
  );
}

const MENU_DATA = [
  {
    category: "Café & Boissons",
    items: [
      { name: "Espresso", desc: "Origine du mois, profil chocolat / fruits rouges", price: "2,00 €" },
      { name: "Flat White", desc: "Double ristretto, lait micro-texturé", price: "4,50 €" },
      { name: "Filtre V60", desc: "Préparation lente, en single origin", price: "3,80 €" },
      { name: "Cold Brew", desc: "Infusion à froid, 18h d'attente", price: "3,50 €" },
      { name: "Nos Thés & Infusions Bio", desc: "Sélection bio, en vrac", price: "2,80 €" },
    ],
  },
  {
    category: "Pâtisseries",
    items: [
      { name: "Cookie du jour", desc: "Cuit du jour, sans gluten", price: "2,80 €" },
      { name: "Banana bread", desc: "Bananes bio, beurre noisette", price: "2,20 €" },
      { name: "Cinnamon roll", desc: "Glaçage vanille, croustillant", price: "3,50 €" },
      { name: "Granola maison", desc: "Yaourt fermier, fruits de saison", price: "4,90 €" },
    ],
  },
  {
    category: "Plats légers",
    items: [
      { name: "Tartine du jour", desc: "Pain au levain, garniture changeante", price: "7,50 €" },
      { name: "Bowl du cycliste", desc: "Quinoa, légumes rôtis, œuf mollet, avocat", price: "9,90 €" },
      { name: "Soupe de saison", desc: "Servie avec pain au levain", price: "6,50 €" },
      { name: "Croque-monsieur revisité", desc: "Comté, jambon, béchamel truffe", price: "8,50 €" },
    ],
  },
];

function MenuCategory({ category, idx }) {
  return (
    <motion.div
      data-testid={`menu-cat-${idx}`}
      initial="hidden"
      whileInView="visible"
      viewport={VIEWPORT_ONCE_20}
      variants={fadeUp}
      custom={idx}
    >
      <h3 className="font-display text-2xl font-medium pb-4 border-b border-[#E5E3DB]">
        {category.category}
      </h3>
      <ul className="mt-6 space-y-6">
        {category.items.map((it) => (
          <li key={it.name} className="flex justify-between gap-6">
            <div>
              <p className="font-medium text-[#1C1C1C]">{it.name}</p>
              <p className="text-sm text-[#595959] mt-1 leading-relaxed">{it.desc}</p>
            </div>
            <span className="font-display font-medium text-[#1C1C1C] whitespace-nowrap">{it.price}</span>
          </li>
        ))}
      </ul>
    </motion.div>
  );
}

function MenuSection() {
  return (
    <section id="cafe" className="py-20 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={VIEWPORT_ONCE_30}
          variants={fadeUp}
          className="max-w-3xl"
        >
          <span className="eyebrow">
            Pause café{" "}
            <span className="ml-2 inline-block bg-[#C46D5E] text-[#FAF9F6] text-[10px] font-bold tracking-[0.2em] px-2.5 py-0.5 rounded-full align-middle">
              SOON
            </span>
          </span>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-semibold mt-4">
            Pendant qu'on s'occupe de votre vélo.
          </h2>
          <p className="text-[#595959] mt-5 leading-relaxed">
            Un café de spécialité, quelques pâtisseries du jour et de quoi déjeuner sur le pouce.
            La carte est courte, à l'image de l'atelier : peu, mais bien.
          </p>
          <p className="text-[#595959] mt-4 leading-relaxed">
            Notre café provient de la torréfaction artisanale <strong className="font-semibold text-[#1C1C1C]">Anne Caron</strong>,
            torréfactrice locale en Île-de-France. Un grain sélectionné en circuits courts,
            torréfié à la main, pour une tasse à la fois ronde, vivante et sincère.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-10 lg:gap-14 mt-16">
          {MENU_DATA.map((cat, idx) => (
            <MenuCategory key={cat.category} category={cat} idx={idx} />
          ))}
        </div>
      </div>
    </section>
  );
}

const SERVICES = [
  { icon: Wrench, title: "Révision complète", desc: "Transmission, freins, roues, direction. Diagnostic offert.", price: "49 €" },
  { icon: Bike, title: "Montage & réglage", desc: "Montage neuf, réglage tubeless, géométrie, position.", price: "89 €" },
  { icon: Wrench, title: "Dépannage express", desc: "Crevaison, câble, patinage : repartez dans la journée.", price: "à partir de 18 €" },
];

function AtelierIntro() {
  return (
    <motion.div
      className="lg:col-span-5"
      initial="hidden"
      whileInView="visible"
      viewport={VIEWPORT_ONCE_30}
      variants={fadeUp}
    >
      <span className="eyebrow">L'atelier</span>
      <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-semibold mt-4">
        Un atelier mécanique exigeant, au cœur de la station.
      </h2>
      <p className="text-[#595959] mt-5 leading-relaxed">
        Nos mécaniciens prennent soin de chaque monture comme si c'était la leur. Diagnostic
        offert, devis transparent, délais respectés. Que vous rouliez Cinelli, Mondraker,
        WELT, UTO, Benno — ou toute autre marque — votre vélo est entre de bonnes mains.
      </p>
      <a href="#contact" className="btn-primary mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold" data-testid="atelier-cta">
        Demander un rendez-vous <ArrowUpRight className="h-4 w-4" />
      </a>
    </motion.div>
  );
}

function ServiceCard({ service, index }) {
  const Icon = service.icon;
  return (
    <motion.div
      className="bento-card p-8"
      initial="hidden"
      whileInView="visible"
      viewport={VIEWPORT_ONCE_30}
      variants={fadeUp}
      custom={index}
      data-testid={`service-card-${index}`}
    >
      <Icon className="h-7 w-7 text-[#C46D5E]" />
      <h3 className="font-display text-xl font-medium mt-5">{service.title}</h3>
      <p className="text-sm text-[#595959] mt-2 leading-relaxed">{service.desc}</p>
      <p className="font-display text-base font-semibold text-[#1C1C1C] mt-4">{service.price}</p>
    </motion.div>
  );
}

function Atelier() {
  return (
    <section id="atelier" className="py-20 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          <AtelierIntro />
          <motion.div
            className="lg:col-span-7 image-zoom h-[420px]"
            {...FADE_IN_IMAGE}
          >
            <img src={IMAGES.workshop} alt="Mécanicien vélo" className="w-full h-full object-cover" />
          </motion.div>
        </div>

        <div className="grid md:grid-cols-3 gap-5 mt-12">
          {SERVICES.map((s, i) => (
            <ServiceCard key={s.title} service={s} index={i} />
          ))}
        </div>

        <VaeCertifications />
      </div>
    </section>
  );
}

const VAE_BRANDS = ["Shimano", "Bosch", "Yamaha"];

function VaeCertifications() {
  return (
    <motion.div
      data-testid="vae-certifications"
      initial="hidden"
      whileInView="visible"
      viewport={VIEWPORT_ONCE_30}
      variants={fadeUp}
      className="mt-8 bg-[#1C1C1C] text-[#FAF9F6] rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center gap-8"
    >
      <div className="flex items-center gap-4 shrink-0">
        <div className="h-14 w-14 rounded-full bg-[#C46D5E]/20 flex items-center justify-center shrink-0">
          <Zap className="h-7 w-7 text-[#C46D5E]" />
        </div>
        <div>
          <span className="text-xs tracking-[0.22em] uppercase font-bold text-[#C46D5E]">VAE</span>
          <h3 className="font-display text-2xl font-medium mt-1">Atelier certifié</h3>
        </div>
      </div>

      <div className="flex-1">
        <p className="text-sm text-[#FAF9F6]/70 leading-relaxed">
          Notre atelier est certifié pour l'entretien et le diagnostic des motorisations de vélos
          à assistance électrique des plus grandes marques :
        </p>
        <div className="flex flex-wrap gap-3 mt-4">
          {VAE_BRANDS.map((brand) => (
            <span
              key={brand}
              data-testid={`vae-cert-${brand.toLowerCase()}`}
              className="inline-flex items-center gap-2 bg-[#FAF9F6]/10 border border-[#FAF9F6]/20 rounded-full px-4 py-2 text-sm font-semibold"
            >
              <BadgeCheck className="h-4 w-4 text-[#C46D5E]" />
              {brand}
            </span>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

const BRANDS = [
  {
    name: "Cinelli",
    tagline: "Gravel · Urbain · Aventure premium",
    desc: "L'âme italienne du cyclisme. Cadres travaillés, géométries vivantes, finitions artisanales pour rouler partout — du pavé bruxellois aux chemins de Chevreuse.",
    price: "à partir de 1 290 €",
    image: IMAGES.bikeGravel,
    accent: "Italie",
    caption: "Cinelli Zydeco — gravel acier, frame italien",
  },
  {
    name: "Mondraker",
    tagline: "VTT premium · Trail · Enduro",
    desc: "La référence VTT espagnole. Géométrie Forward, suspensions de pointe, finition d'usine. Pour celles et ceux qui ne transigent pas avec la descente.",
    price: "à partir de 1 550 €",
    image: IMAGES.bikeMtb,
    accent: "Premium",
    caption: "Mondraker — VTT premium, géométrie Forward",
  },
  {
    name: "WELT",
    tagline: "Gravel · VTT polyvalent",
    desc: "Le bon rapport performance / prix pour celles et ceux qui veulent un gravel ou un VTT solide, prêt à encaisser les sorties du week-end et les trajets quotidiens.",
    price: "à partir de 460 €",
    image: IMAGES.bikeUrban,
    accent: "Performance",
    caption: "WELT — gravel & VTT polyvalents",
  },
  {
    name: "UTO",
    tagline: "Vélos pliants",
    desc: "La mobilité urbaine repensée. Pliage rapide, transport facile dans le RER C, l'assurance d'un vélo qui rentre dans tous les trajets.",
    price: "à partir de 1 650 €",
    image: IMAGES.bikeFolding,
    accent: "Urbain",
    caption: "UTO — pliant urbain compact",
  },
  {
    name: "Benno",
    tagline: "Boost · Cargo électrique · VAE familial",
    desc: "Le Benno Boost — vélo cargo électrique californien, motorisation Bosch Performance CX, charge utile généreuse — courses, enfants, balades, tout y passe. Le vélo qui remplace la voiture.",
    price: "4 450 €",
    image: IMAGES.bikeCargo,
    accent: "Boost",
    caption: "Benno Boost — cargo électrique Bosch CX",
  },
];

const TAGLINE_STYLE = { color: "#4A5D23" };

function BrandCard({ brand, index, isWide }) {
  return (
    <motion.div
      data-testid={`brand-card-${index}`}
      className={`bento-card overflow-hidden flex flex-col ${
        isWide ? "md:col-span-2 md:flex-row" : ""
      }`}
      initial="hidden"
      whileInView="visible"
      viewport={VIEWPORT_ONCE_20}
      variants={fadeUp}
      custom={index}
    >
      <div className={`image-zoom rounded-none relative ${
        isWide ? "h-72 md:h-auto md:w-1/2 md:flex-shrink-0" : "h-72"
      }`}>
        <img src={brand.image} alt={brand.name} className="w-full h-full object-cover" />
        {brand.caption && (
          <span
            data-testid={`brand-caption-${index}`}
            className="absolute bottom-3 left-3 bg-[#1C1C1C]/80 text-[#FAF9F6] text-xs tracking-wide px-3 py-1.5 rounded-full backdrop-blur-sm"
          >
            {brand.caption}
          </span>
        )}
      </div>
      <div className="p-8 md:p-10 flex flex-col flex-1">
        <div className="flex items-center justify-between gap-4">
          <h3 className="font-display text-3xl font-bold tracking-tight">{brand.name}</h3>
          <span className="text-xs uppercase tracking-[0.18em] text-[#595959] border border-[#E5E3DB] px-3 py-1 rounded-full">
            {brand.accent}
          </span>
        </div>
        <p className="eyebrow mt-2" style={TAGLINE_STYLE}>{brand.tagline}</p>
        <p className="text-sm text-[#595959] mt-4 leading-relaxed flex-1">{brand.desc}</p>
        <div className="flex items-center justify-between mt-6 pt-6 border-t border-[#E5E3DB]">
          <p className="font-display font-semibold text-[#1C1C1C]">{brand.price}</p>
          <a
            href="#contact"
            data-testid={`brand-cta-${index}`}
            className="text-sm font-semibold inline-flex items-center gap-1 text-[#C46D5E] hover:text-[#B35C4D]"
          >
            Essayer en magasin <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </div>
    </motion.div>
  );
}

function VelosHeader() {
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={VIEWPORT_ONCE_30}
      variants={fadeUp}
      className="flex flex-wrap items-end justify-between gap-6 max-w-5xl"
    >
      <div>
        <span className="eyebrow">Le showroom</span>
        <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-semibold mt-4">
          Cinq marques, choisies une à une.
        </h2>
      </div>
      <p className="text-[#595959] max-w-md">
        On ne vend que ce que l'on roule. Essais en magasin, conseil sur la taille, la
        géométrie et l'usage. Reprise de votre ancien vélo possible.
      </p>
    </motion.div>
  );
}

function VelosCustomCta() {
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={VIEWPORT_ONCE}
      variants={fadeUp}
      className="mt-12 bg-[#1C1C1C] text-[#FAF9F6] rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6"
    >
      <div>
        <h3 className="font-display text-2xl font-medium">Vous cherchez un modèle précis ?</h3>
        <p className="text-sm text-[#FAF9F6]/70 mt-2 max-w-xl">
          Montage à la carte, commande sur stock Outlet, reprise — on en discute autour d'un café.
        </p>
      </div>
      <a
        href="#contact"
        data-testid="velos-contact-cta"
        className="btn-primary px-6 py-3 rounded-full text-sm font-semibold inline-flex items-center gap-2 whitespace-nowrap"
      >
        Nous contacter <ArrowUpRight className="h-4 w-4" />
      </a>
    </motion.div>
  );
}

function Velos() {
  return (
    <section id="velos" className="py-20 md:py-32 bg-[#F2F0EA]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <VelosHeader />

        <div className="grid md:grid-cols-2 gap-5 mt-14">
          {BRANDS.map((b, i) => (
            <BrandCard key={b.name} brand={b} index={i} isWide={i === 4} />
          ))}
        </div>

        <VelosCustomCta />
      </div>
    </section>
  );
}


function Galerie() {
  const items = [
    { src: IMAGES.cafeInterior, alt: "Intérieur cosy", className: "md:col-span-2 md:row-span-2 h-72 md:h-auto" },
    { src: IMAGES.coffee, alt: "Latte art", className: "h-56 md:h-72" },
    { src: IMAGES.cyclistLifestyle, alt: "Pause cycliste", className: "h-56 md:h-72" },
    { src: IMAGES.workshop, alt: "Atelier", className: "h-56 md:h-72" },
    { src: IMAGES.hero, alt: "Sortie gravel", className: "h-56 md:h-72" },
  ];
  return (
    <section id="galerie" className="py-20 md:py-32 bg-[#F2F0EA]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={VIEWPORT_ONCE_30}
          variants={fadeUp}
          className="flex flex-wrap items-end justify-between gap-6"
        >
          <div>
            <span className="eyebrow">Galerie</span>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-semibold mt-4">
              Quelques échappées.
            </h2>
          </div>
          <p className="text-[#595959] max-w-md">
            Du grain de café à la roue qui tourne, l'ambiance de la station, capturée au jour le jour.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-12">
          {items.map((it, i) => (
            <motion.div
              key={it.src}
              className={`image-zoom ${it.className}`}
              initial={GALLERY_INITIAL}
              whileInView={GALLERY_WHILE_IN_VIEW}
              transition={galleryTransition(i)}
              viewport={VIEWPORT_ONCE}
              data-testid={`gallery-item-${i}`}
            >
              <img src={it.src} alt={it.alt} className="w-full h-full object-cover" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

const EMPTY_CONTACT_FORM = { name: "", email: "", phone: "", subject: "", message: "" };

function ContactInfoBlock() {
  return (
    <div className="mt-10 space-y-5">
      <div className="flex gap-4">
        <MapPin className="h-5 w-5 text-[#C46D5E] mt-0.5 shrink-0" />
        <div>
          <p className="font-medium">1 rue de Draveil</p>
          <p className="text-sm text-[#595959]">91260 Juvisy-sur-Orge</p>
        </div>
      </div>
      <div className="flex gap-4">
        <Clock className="h-5 w-5 text-[#C46D5E] mt-0.5 shrink-0" />
        <div className="text-sm">
          <p><span className="font-medium">Mar – Ven</span> · 10h30 – 20h00</p>
          <p><span className="font-medium">Sam &amp; Lun</span> · 10h30 – 18h30</p>
          <p className="text-[#595959]">Fermé le dimanche</p>
        </div>
      </div>
      <div className="flex gap-4">
        <Phone className="h-5 w-5 text-[#C46D5E] mt-0.5 shrink-0" />
        <a href="tel:+33183539370" className="text-sm hover:text-[#C46D5E] transition-colors">01 83 53 93 70</a>
      </div>
      <div className="flex gap-4">
        <Mail className="h-5 w-5 text-[#C46D5E] mt-0.5 shrink-0" />
        <a href="mailto:contact@mystation.tech" className="text-sm hover:text-[#C46D5E] transition-colors">contact@mystation.tech</a>
      </div>
    </div>
  );
}

function ContactForm() {
  const [form, setForm] = useState(EMPTY_CONTACT_FORM);
  const [submitting, setSubmitting] = useState(false);

  const updateField = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Merci de remplir nom, email et message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message envoyé. Nous revenons vers vous très vite.");
      setForm(EMPTY_CONTACT_FORM);
    } catch (err) {
      toast.error("Une erreur est survenue. Réessayez dans un instant.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-[#FAF9F6] rounded-2xl border border-[#E5E3DB] p-8 md:p-10" data-testid="contact-form">
      <h3 className="font-display text-2xl font-medium">Écrivez-nous</h3>
      <div className="grid md:grid-cols-2 gap-4 mt-6">
        <input type="text" placeholder="Votre nom" value={form.name} onChange={updateField("name")} className="input-organic rounded-xl px-4 py-3 text-sm" data-testid="contact-name" required />
        <input type="email" placeholder="Votre email" value={form.email} onChange={updateField("email")} className="input-organic rounded-xl px-4 py-3 text-sm" data-testid="contact-email" required />
        <input type="tel" placeholder="Téléphone (optionnel)" value={form.phone} onChange={updateField("phone")} className="input-organic rounded-xl px-4 py-3 text-sm" data-testid="contact-phone" />
        <input type="text" placeholder="Sujet" value={form.subject} onChange={updateField("subject")} className="input-organic rounded-xl px-4 py-3 text-sm" data-testid="contact-subject" />
      </div>
      <textarea placeholder="Votre message" rows={5} value={form.message} onChange={updateField("message")} className="input-organic rounded-xl px-4 py-3 text-sm w-full mt-4 resize-none" data-testid="contact-message" required />
      <button type="submit" disabled={submitting} className="btn-primary mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold disabled:opacity-60" data-testid="contact-submit">
        {submitting ? "Envoi…" : "Envoyer le message"} <Send className="h-4 w-4" />
      </button>
    </form>
  );
}

function NewsletterForm() {
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubscribing(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("Inscription enregistrée. À très vite dans la boîte mail.");
      setEmail("");
    } catch (err) {
      toast.error("Email invalide ou erreur réseau.");
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-6 bg-[#1C1C1C] rounded-2xl p-8 md:p-10 text-[#FAF9F6]" data-testid="newsletter-form">
      <span className="text-xs tracking-[0.22em] uppercase font-bold text-[#C46D5E]">Newsletter</span>
      <h3 className="font-display text-2xl font-medium mt-3">Nos sorties, nos cafés, nos news.</h3>
      <p className="text-sm text-[#FAF9F6]/70 mt-2">Une lettre par mois, jamais plus, jamais de spam.</p>
      <div className="flex flex-col sm:flex-row gap-3 mt-6">
        <input type="email" placeholder="vous@email.com" value={email} onChange={(e) => setEmail(e.target.value)} className="flex-1 bg-transparent border border-[#FAF9F6]/30 rounded-full px-5 py-3 text-sm placeholder:text-[#FAF9F6]/40 focus:outline-none focus:border-[#C46D5E]" data-testid="newsletter-email" required />
        <button type="submit" disabled={subscribing} className="btn-primary px-6 py-3 rounded-full text-sm font-semibold disabled:opacity-60" data-testid="newsletter-submit-button">
          {subscribing ? "…" : "Je m'inscris"}
        </button>
      </div>
    </form>
  );
}

function ContactSection() {
  return (
    <section id="contact" className="py-20 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          <motion.div
            className="lg:col-span-5"
            initial="hidden"
            whileInView="visible"
            viewport={VIEWPORT_ONCE_30}
            variants={fadeUp}
          >
            <span className="eyebrow">Nous trouver</span>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-semibold mt-4">
              Passez nous voir.
            </h2>
            <p className="text-[#595959] mt-5 leading-relaxed">
              On adore parler vélo, café, et tout le reste. Écrivez-nous, ou venez à la station.
            </p>

            <ContactInfoBlock />

            <div className="mt-10 rounded-2xl overflow-hidden border border-[#E5E3DB] h-56">
              <iframe
                title="Carte My Station"
                src="https://www.google.com/maps?q=1+rue+de+Draveil,+91260+Juvisy-sur-Orge&output=embed"
                className="w-full h-full border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </motion.div>

          <motion.div
            className="lg:col-span-7"
            initial="hidden"
            whileInView="visible"
            viewport={VIEWPORT_ONCE_20}
            variants={fadeUp}
            custom={1}
          >
            <ContactForm />
            <NewsletterForm />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-[#1C1C1C] text-[#FAF9F6] pt-20 pb-10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-[#FAF9F6] flex items-center justify-center">
                <Bike className="h-4 w-4 text-[#1C1C1C]" />
              </div>
              <span className="font-display text-xl font-bold">My Station</span>
            </div>
            <p className="text-sm text-[#FAF9F6]/70 mt-4 max-w-sm">
              Café-vélo de Juvisy-sur-Orge. Spécialité, atelier, accueil vélo, communauté.
            </p>
          </div>
          <div>
            <p className="text-xs tracking-[0.22em] uppercase font-bold text-[#FAF9F6]/50">Adresse</p>
            <p className="text-sm mt-3">1 rue de Draveil</p>
            <p className="text-sm">91260 Juvisy-sur-Orge</p>
          </div>
          <div>
            <p className="text-xs tracking-[0.22em] uppercase font-bold text-[#FAF9F6]/50">Suivez-nous</p>
            <div className="flex flex-col gap-3 mt-3 text-sm">
              <a href="https://instagram.com" target="_blank" rel="noreferrer" className="hover:text-[#C46D5E] inline-flex items-center gap-2"><Instagram className="h-4 w-4" /> Instagram</a>
              <a href="https://strava.com" target="_blank" rel="noreferrer" className="hover:text-[#C46D5E] inline-flex items-center gap-2">Strava</a>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-10 border-t border-[#FAF9F6]/10">
          <p className="font-display text-[18vw] leading-none font-extrabold text-[#FAF9F6]/5 text-center select-none tracking-tighter">
            MY STATION
          </p>
        </div>

        <div className="flex flex-col md:flex-row justify-between gap-4 mt-10 text-xs text-[#FAF9F6]/50">
          <p>© {new Date().getFullYear()} My Station — Tous droits réservés.</p>
          <p>Conçu à Juvisy-sur-Orge · Ride with mates.</p>
        </div>
      </div>
    </footer>
  );
}

function App() {
  useEffect(() => {
    document.title = "My Station — Café Vélo · Juvisy-sur-Orge";
  }, []);

  return (
    <div className="App">
      <Toaster position="bottom-right" richColors />
      <Header />
      <main>
        <Hero />
        <About />
        <Atelier />
        <Velos />
        <MenuSection />
        <Galerie />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
